import React from 'react';
import { TableBlock, TableRow } from './Table.styled';
import TableCellComponent from './TableCellComponent';

const mapCellAttributes = (cellData, rows, cols) => {
  const attributes = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => ({})),
  );

  cellData?.forEach(({ col, row, ...props }) => {
    attributes[row][col] = { ...props };
  });

  return attributes;
};

const generateBorderStyles = (borders) => {
  if (!borders) return {};
  return Object.fromEntries(
    ['top', 'bottom', 'left', 'right'].map((side) => [
      `border${side.charAt(0).toUpperCase() + side.slice(1)}`,
      borders[side]
        ? `${borders[side].width}px solid ${borders[side].color}`
        : undefined,
    ]),
  );
};

const Table = ({ value }) => {
  const {
    data: initData,
    cell = [],
    firstRowIsTableHeader = false,
    firstColIsHeader = false,
    colWidths = [],
    rowHeights = [],
  } = value || {};
  let data = initData;
  if (!data?.length) {
    const defaultData = Array.from({ length: 8 }, () =>
      Array.from({ length: 6 }, () => ({})),
    );
    data = defaultData;
  }
  const numRows = data?.length;
  const numCols = data[0]?.length || 0;

  const cellAttributes = mapCellAttributes(cell, numRows, numCols);
  return (
    <TableBlock firstRowHeader={firstRowIsTableHeader}>
      <tbody>
        {data.map((rowData, rowIndex) => (
          <TableRow key={rowIndex} firstColHeader={firstColIsHeader}>
            {rowData.map((cellValue, colIndex) => {
              const { colspan, rowspan, borders, ...rest } =
                cellAttributes[rowIndex][colIndex] || {};

              // Skip rendering cells that are covered by rowspan/colspan
              if (cellAttributes[rowIndex][colIndex].skip) {
                return null;
              }

              const computedColWidth = colWidths?.[colIndex];
              const computedRowHeight = rowHeights?.[rowIndex];

              const styles = {
                ...generateBorderStyles(borders),
                backgroundColor: rest.color,
                color: rest.fontColor,
              };

              // Mark cells covered by rowspan/colspan as skipped
              if (rowspan > 1 || colspan > 1) {
                for (let r = rowIndex; r < rowIndex + rowspan; r += 1) {
                  for (let c = colIndex; c < colIndex + colspan; c += 1) {
                    if (r !== rowIndex || c !== colIndex) {
                      cellAttributes[r][c] = { skip: true };
                    }
                  }
                }
              }

              return (
                <TableCellComponent
                  key={colIndex}
                  cellValue={cellValue}
                  cellClassName={rest.className || ''}
                  style={styles}
                  colspan={colspan > 1 ? colspan : undefined}
                  rowspan={rowspan > 1 ? rowspan : undefined}
                  colWidth={computedColWidth}
                  rowHeight={computedRowHeight}
                />
              );
            })}
          </TableRow>
        ))}
      </tbody>
    </TableBlock>
  );
};

export default Table;
